/**
 * Data Service - Handles loading and caching of financial data
 */

import type {
  FinancialDataset,
  TradingMultiplesDataset,
  OperatingStatsDataset,
  BusinessDescriptionData,
  HeadquartersData,
  DataYear,
  Product,
  Competitor,
  MATransactionData,
  MACompany,
  MATransaction
} from '../types';

/**
 * Custom error class for data loading errors
 */
export class DataLoadError extends Error {
  constructor(
    message: string,
    public readonly dataType: string,
    public readonly cause?: Error
  ) {
    super(message);
    this.name = 'DataLoadError';
  }
}

/**
 * Service for loading and managing financial data
 * Implements caching to avoid repeated fetches
 */
export class DataService {
  private cache: Map<string, unknown> = new Map();
  private baseDataPath = '/data';

  /**
   * Load financial data for a specific year
   */
  async loadFinancialData(year: DataYear): Promise<FinancialDataset> {
    const cacheKey = `financial-${year}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as FinancialDataset;
    }

    try {
      const data = await this.fetchJSON<FinancialDataset>(`${this.baseDataPath}/march${year}.json`);

      // Validate data structure
      if (!this.isFinancialDataset(data)) {
        throw new Error('Invalid financial data structure');
      }

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new DataLoadError(
        `Failed to load financial data for ${year}`,
        'FinancialDataset',
        error as Error
      );
    }
  }

  /**
   * Load trading multiples for a specific year
   */
  async loadTradingMultiples(year: DataYear): Promise<TradingMultiplesDataset> {
    const cacheKey = `tradingMultiples-${year}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as TradingMultiplesDataset;
    }

    try {
      const data = await this.fetchJSON<TradingMultiplesDataset>(
        `${this.baseDataPath}/march${year}TradingMultiples.json`
      );

      // Validate data structure
      if (!this.isTradingMultiplesDataset(data)) {
        throw new Error('Invalid trading multiples data structure');
      }

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new DataLoadError(
        `Failed to load trading multiples for ${year}`,
        'TradingMultiplesDataset',
        error as Error
      );
    }
  }

  /**
   * Load operating statistics for a specific year
   */
  async loadOperatingStats(year: DataYear): Promise<OperatingStatsDataset> {
    const cacheKey = `operatingStats-${year}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as OperatingStatsDataset;
    }

    try {
      const data = await this.fetchJSON<OperatingStatsDataset>(
        `${this.baseDataPath}/march${year}OperatingStats.json`
      );

      // Validate data structure
      if (!this.isOperatingStatsDataset(data)) {
        throw new Error('Invalid operating stats data structure');
      }

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new DataLoadError(
        `Failed to load operating stats for ${year}`,
        'OperatingStatsDataset',
        error as Error
      );
    }
  }

  /**
   * Load business descriptions for all companies
   */
  async loadBusinessDescriptions(): Promise<BusinessDescriptionData> {
    const cacheKey = 'businessDescriptions';

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as BusinessDescriptionData;
    }

    try {
      const data = await this.fetchJSON<BusinessDescriptionData>(
        `${this.baseDataPath}/businessDescriptions.json`
      );

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new DataLoadError(
        'Failed to load business descriptions',
        'BusinessDescriptionData',
        error as Error
      );
    }
  }

  /**
   * Load headquarters information for all companies
   */
  async loadHeadquarters(): Promise<HeadquartersData> {
    const cacheKey = 'headquarters';

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as HeadquartersData;
    }

    try {
      const data = await this.fetchJSON<HeadquartersData>(
        `${this.baseDataPath}/headquarters.json`
      );

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new DataLoadError(
        'Failed to load headquarters data',
        'HeadquartersData',
        error as Error
      );
    }
  }

  /**
   * Load target companies list
   */
  async loadTargetCompanies(): Promise<string[]> {
    const cacheKey = 'targetCompanies';

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as string[];
    }

    try {
      const data = await this.fetchJSON<string[]>(
        `${this.baseDataPath}/targetCompanies.json`
      );

      if (!Array.isArray(data)) {
        throw new Error('Invalid target companies data structure');
      }

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new DataLoadError(
        'Failed to load target companies',
        'TargetCompanies',
        error as Error
      );
    }
  }

  /**
   * Load products list
   */
  async loadProducts(): Promise<Product[]> {
    const cacheKey = 'products';

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as Product[];
    }

    try {
      const data = await this.fetchJSON<Product[]>(
        `${this.baseDataPath}/products.json`
      );

      if (!Array.isArray(data)) {
        throw new Error('Invalid products data structure');
      }

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new DataLoadError(
        'Failed to load products',
        'Products',
        error as Error
      );
    }
  }

  /**
   * Load competitors list
   */
  async loadCompetitors(): Promise<Competitor[]> {
    const cacheKey = 'competitors';

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as Competitor[];
    }

    try {
      const data = await this.fetchJSON<Competitor[]>(
        `${this.baseDataPath}/competitors.json`
      );

      if (!Array.isArray(data)) {
        throw new Error('Invalid competitors data structure');
      }

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      throw new DataLoadError(
        'Failed to load competitors',
        'Competitors',
        error as Error
      );
    }
  }

  /**
   * Load M&A companies index (lightweight metadata only)
   */
  async loadMACompaniesIndex(): Promise<MACompany[]> {
    const cacheKey = 'ma-companies-index';

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as MACompany[];
    }

    try {
      const data = await this.fetchJSON<{ companies: MACompany[] }>(
        `${this.baseDataPath}/ma-transactions/index.json`
      );

      if (!Array.isArray(data.companies)) {
        throw new Error('Invalid M&A companies index structure');
      }

      // Add empty transactionIds array for compatibility
      const companies = data.companies.map(c => ({
        ...c,
        transactionIds: []
      }));

      this.cache.set(cacheKey, companies);
      return companies;
    } catch (error) {
      throw new DataLoadError(
        'Failed to load M&A companies index',
        'MACompaniesIndex',
        error as Error
      );
    }
  }

  /**
   * Load transactions for a specific company
   */
  async loadCompanyTransactions(ticker: string): Promise<MATransaction[]> {
    const cacheKey = `ma-transactions-${ticker.toLowerCase()}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as MATransaction[];
    }

    try {
      const data = await this.fetchJSON<{
        ticker: string;
        companyName: string;
        transactions: Record<string, MATransaction>;
      }>(`${this.baseDataPath}/ma-transactions/companies/${ticker.toLowerCase()}.json`);

      if (!data.transactions || typeof data.transactions !== 'object') {
        throw new Error('Invalid company transactions structure');
      }

      // Convert transactions object to array
      const transactions = Object.values(data.transactions);

      this.cache.set(cacheKey, transactions);
      return transactions;
    } catch (error) {
      throw new DataLoadError(
        `Failed to load transactions for ${ticker}`,
        'CompanyTransactions',
        error as Error
      );
    }
  }

  /**
   * Load M&A transactions data (loads all companies)
   * This method loads all company transaction files and combines them
   * for backward compatibility with existing code
   */
  async loadMATransactions(): Promise<MATransactionData> {
    const cacheKey = 'ma-transactions';

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) as MATransactionData;
    }


    try {
      // Load index to get list of companies
      const indexPath = `${this.baseDataPath}/ma-transactions/index.json`;
      const indexData = await this.fetchJSON<{ companies: MACompany[] }>(indexPath);

      if (!Array.isArray(indexData.companies)) {
        throw new Error('Invalid M&A companies index structure');
      }


      // Load all company transaction files
      const allTransactions: Record<string, MATransaction> = {};
      const companies: MACompany[] = [];

      for (const company of indexData.companies) {
        const ticker = company.ticker.toLowerCase();
        const companyPath = `${this.baseDataPath}/ma-transactions/companies/${ticker}.json`;

        try {
          const companyData = await this.fetchJSON<{
            ticker: string;
            companyName: string;
            transactions: Record<string, MATransaction>;
          }>(companyPath);

          // Add transactions to combined map
          Object.assign(allTransactions, companyData.transactions);

          // Add company with transaction IDs
          companies.push({
            ticker: company.ticker,
            name: company.name,
            exchange: company.exchange,
            fullName: company.fullName,
            transactionIds: Object.keys(companyData.transactions)
          });
        } catch (error) {
          console.error(`[DataService] Failed to load transactions for ${company.ticker}:`, error);
        }
      }


      const data: MATransactionData = {
        companies,
        transactions: allTransactions
      };

      if (!this.isMATransactionData(data)) {
        throw new Error('Invalid M&A transactions data structure');
      }

      this.cache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error('[DataService] Error loading MA transactions:', error);
      throw new DataLoadError(
        'Failed to load M&A transactions',
        'MATransactionData',
        error as Error
      );
    }
  }

  /**
   * Search M&A companies by ticker or name (case-insensitive)
   */
  async searchMACompaniesByTickerOrName(query: string, searchType: 'tickers' | 'name' = 'tickers'): Promise<MACompany[]> {
    const data = await this.loadMATransactions();
    const normalizedQuery = query.toLowerCase().trim();

    if (!normalizedQuery) {
      return [];
    }

    return data.companies.filter((company: MACompany) => {
      if (searchType === 'tickers') {
        // Exclude private companies with synthetic tickers (showTickerInFullName: false)
        // These are internal identifiers, not real exchange tickers
        if (company.showTickerInFullName === false) {
          return false;
        }

        // Ticker search: match only ticker field with exact or starts-with logic
        const ticker = company.ticker.toLowerCase();
        const exactMatch = ticker === normalizedQuery;
        const startsWithMatch = ticker.startsWith(normalizedQuery);
        return exactMatch || startsWithMatch;
      } else {
        // Name search: match name/fullName with word boundary to avoid substring matches
        const name = company.name.toLowerCase();
        const fullName = company.fullName.toLowerCase();

        // Use word boundary regex to avoid matching "on" inside "Corporation"
        const wordBoundaryRegex = new RegExp(`\\b${normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');

        return wordBoundaryRegex.test(name) || wordBoundaryRegex.test(fullName);
      }
    });
  }

  /**
   * Get transactions for a specific company with optional date filtering
   * Optimized to load only the specific company's transactions
   */
  async getTransactionsForCompany(
    ticker: string,
    fromDate?: string,
    toDate?: string
  ): Promise<MATransaction[]> {
    try {
      // Load only this company's transactions (much faster than loading all)
      let transactions = await this.loadCompanyTransactions(ticker);

      // Apply date filtering if provided
      if (fromDate || toDate) {
        transactions = transactions.filter(txn => {
          const announcedDate = txn.announcedDate;

          if (fromDate && announcedDate < fromDate) {
            return false;
          }

          if (toDate && announcedDate > toDate) {
            return false;
          }

          return true;
        });
      }

      // Sort by date descending (most recent first)
      transactions.sort((a, b) => b.announcedDate.localeCompare(a.announcedDate));

      return transactions;
    } catch (error) {
      // If company file doesn't exist, return empty array
      console.warn(`No transactions found for ${ticker}:`, error);
      return [];
    }
  }

  /**
   * Get a single transaction by ID
   */
  async getMATransactionById(transactionId: string): Promise<MATransaction | null> {
    const data = await this.loadMATransactions();
    return data.transactions[transactionId] || null;
  }

  /**
   * Get multiple transactions by IDs
   */
  async getMATransactionsByIds(transactionIds: string[]): Promise<MATransaction[]> {
    const data = await this.loadMATransactions();

    const results = transactionIds
      .map(id => {
        const txn = data.transactions[id];
        if (!txn) {
          console.warn('[DataService] Transaction not found:', id);
        }
        return txn;
      })
      .filter((txn): txn is MATransaction => txn !== undefined)
      .sort((a, b) => b.announcedDate.localeCompare(a.announcedDate));

    return results;
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear specific cached data by key pattern
   */
  clearCacheByPattern(pattern: string): void {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Generic JSON fetch helper
   */
  private async fetchJSON<T>(path: string): Promise<T> {
    const response = await fetch(path);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json() as Promise<T>;
  }

  /**
   * Type guard for FinancialDataset
   */
  private isFinancialDataset(data: unknown): data is FinancialDataset {
    if (typeof data !== 'object' || data === null) return false;

    const dataset = data as FinancialDataset;
    return (
      Array.isArray(dataset.companies) &&
      typeof dataset.summary === 'object' &&
      dataset.summary !== null &&
      'high' in dataset.summary &&
      'low' in dataset.summary &&
      'mean' in dataset.summary &&
      'median' in dataset.summary
    );
  }

  /**
   * Type guard for TradingMultiplesDataset
   */
  private isTradingMultiplesDataset(data: unknown): data is TradingMultiplesDataset {
    if (typeof data !== 'object' || data === null) return false;

    const dataset = data as TradingMultiplesDataset;
    return (
      Array.isArray(dataset.companies) &&
      typeof dataset.summary === 'object' &&
      dataset.summary !== null &&
      'high' in dataset.summary &&
      'low' in dataset.summary &&
      'mean' in dataset.summary &&
      'median' in dataset.summary
    );
  }

  /**
   * Type guard for OperatingStatsDataset
   */
  private isOperatingStatsDataset(data: unknown): data is OperatingStatsDataset {
    if (typeof data !== 'object' || data === null) return false;

    const dataset = data as OperatingStatsDataset;
    return (
      Array.isArray(dataset.companies) &&
      typeof dataset.summary === 'object' &&
      dataset.summary !== null &&
      'high' in dataset.summary &&
      'low' in dataset.summary &&
      'mean' in dataset.summary &&
      'median' in dataset.summary
    );
  }

  /**
   * Type guard for MATransactionData
   */
  private isMATransactionData(data: unknown): data is MATransactionData {
    if (typeof data !== 'object' || data === null) return false;

    const dataset = data as MATransactionData;
    return (
      Array.isArray(dataset.companies) &&
      typeof dataset.transactions === 'object' &&
      dataset.transactions !== null
    );
  }
}
